/**
 * NetworkStatus — 网络状态提示
 *
 * 功能：
 * - 实时监测网络连接状态
 * - 断网时显示红色警告条
 * - 网络恢复时显示绿色提示，2秒后自动消失
 * - 固定在顶部导航栏下方
 * - 支持手动关闭
 *
 * 使用示例：
 * <NetworkStatus />
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import { Wifi, WifiOff, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ★ 常量配置
const RECOVERY_DISPLAY_DURATION = 2000;  // 恢复提示显示时长（ms）

/**
 * Spring 动画配置
 */
const spring = {
  type: 'spring',
  bounce: 0.1,
  duration: 0.4,
};

/**
 * 网络状态提示组件
 */
const NetworkStatus = memo(() => {
  const [isOnline, setIsOnline] = useState(() => {
    // 兼容不支持 onLine 属性的浏览器
    return typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
      ? navigator.onLine
      : true;
  });
  const [showRecovery, setShowRecovery] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // ★ 网络断开处理
  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setShowRecovery(false);
    setIsDismissed(false);
  }, []);

  // ★ 网络恢复处理
  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setShowRecovery(true);
    setIsDismissed(false);

    // 自动隐藏恢复提示
    setTimeout(() => {
      setShowRecovery(false);
    }, RECOVERY_DISPLAY_DURATION);
  }, []);

  // ★ 监听网络状态变化
  useEffect(() => {
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [handleOffline, handleOnline]);

  // ★ 手动关闭
  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  // 断网状态 且 未手动关闭
  if (!isOnline && !isDismissed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={spring}
        className="fixed top-14 left-0 right-0 z-[60]"
        role="alert"
        aria-live="assertive"
      >
        <div className="mx-4 mt-2 rounded-xl overflow-hidden" style={{
          background: 'rgba(127, 29, 29, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
        }}>
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                background: 'rgba(239, 68, 68, 0.2)',
              }}>
                <WifiOff className="w-4 h-4 text-red-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-red-100">网络连接已断开</p>
                <p className="text-xs text-red-300/70 mt-0.5">请检查网络设置，部分内容可能无法加载</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-lg hover:bg-red-500/20 transition-colors"
              aria-label="关闭提示"
            >
              <X className="w-4 h-4 text-red-300" />
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  // 网络恢复提示
  if (isOnline && showRecovery) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={spring}
        className="fixed top-14 left-0 right-0 z-[60]"
        role="status"
        aria-live="polite"
      >
        <div className="mx-4 mt-2 rounded-xl overflow-hidden" style={{
          background: 'rgba(20, 83, 45, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
        }}>
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              background: 'rgba(34, 197, 94, 0.2)',
            }}>
              <Wifi className="w-4 h-4 text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-100">网络已恢复</p>
              <p className="text-xs text-green-300/70 mt-0.5">正在同步最新内容...</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
});

NetworkStatus.displayName = 'NetworkStatus';

export default NetworkStatus;
